import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize, forkJoin } from 'rxjs';
import { Httpservice } from '../../../services/httpservice';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import {
  buildPaginationItems,
  buildPaginationLabel,
  buildQueryString,
  buildTimestampFileName,
  canShowPagination,
  downloadBlob,
  extractPagination,
  getApiErrorMessage,
} from '../../../helper/table-utils';
import { Commonservice } from '../../../services/commonservice';
import { DashboardHeaderComponent } from '../../structure/header/header';
import { DashboardSidebarComponent } from '../../structure/sidebar/sidebar';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { ManageGeneratorAutocompleteComponent } from '../manage-generator/manage-generator-autocomplete';
import {
  DropdownOption,
  ManageSubstationFormValue,
  ManageSubstationGeneratorItem,
  ManageSubstationItem,
  ManageSubstationPagination,
} from './manage-substation.interfaces';
import { createManageSubstationFormValue } from './manage-substation.data';

type ManageSubstationModalMode = 'create' | 'edit' | 'view';
type ManageSubstationSortField = 'station_name' | 'code' | 'province' | 'district';
type ManageSubstationSortOrder = 'asc' | 'desc';
type ManageSubstationAutocompleteEvent = Event | { value?: string; query?: string; originalEvent?: Event | null };
type ManageSubstationDistrictOption = DropdownOption & { stateId: number | null };

const SUBSTATION_ALL_FILTER_VALUE = 'all';
const SUBSTATION_NO_RECORDS_LABEL = 'No records';
const SUBSTATION_SHOWING_RESULTS_LABEL = 'Showing';
const SUBSTATION_RESULTS_LABEL = 'results';
const SUBSTATION_ONLY_XLSX_MESSAGE = 'Only .xlsx files are allowed.';
const SUBSTATION_FAILED_LOAD_STATES_MESSAGE = 'Failed to load states:';
const SUBSTATION_FAILED_LOAD_VOLTAGE_LEVELS_MESSAGE = 'Failed to load voltage levels:';
const SUBSTATION_FAILED_LOAD_DISTRICTS_MESSAGE = 'Failed to load districts:';
const SUBSTATION_FAILED_LOAD_SUBSTATIONS_MESSAGE = 'Failed to load substations:';
const SUBSTATION_FAILED_EXPORT_MESSAGE = 'Failed to export substation data.';
const SUBSTATION_FAILED_TEMPLATE_MESSAGE = 'Failed to download template.';
const SUBSTATION_LOGSHEET_OPTIONS = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
] as const;
const SUBSTATION_GRID_DIVISION_OPTIONS = [
  'Pokhara Grid Division',
  'Hetauda Grid Division',
  'Kohalpur Grid Section',
  'Dhalkebar Grid Branch',
  'Duhabi Grid Division',
  'Attaria Grid Division',
  'Butwal Grid Division',
  'Kathmandu Grid Division',
  'Khimti Grid Section',
] as const;
type ManageSubstationQueryValue = string | number | null | undefined;
type ManageSubstationQueryParams = Record<string, ManageSubstationQueryValue>;

function trimmedRequiredValidator(control: AbstractControl): ValidationErrors | null {
  return typeof control.value === 'string' && control.value.trim() ? null : { required: true };
}

function coordinateRangeValidator(minimum: number, maximum: number): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const coordinate = typeof value === 'number' ? value : Number(value.trim());
    return Number.isFinite(coordinate) && coordinate >= minimum && coordinate <= maximum
      ? null
      : { coordinateRange: { minimum, maximum } };
  };
}

@Component({
  selector: 'app-manage-substation',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, DashboardSidebarComponent, DashboardHeaderComponent, ManageGeneratorAutocompleteComponent],
  templateUrl: './manage-substation.html',
  styleUrl: './manage-substation.scss',
})
export class ManageSubstation  implements OnInit {

protected readonly user = DEFAULT_DASHBOARD_USER;

  protected readonly filterForm: UntypedFormGroup;
  protected readonly substationForm: UntypedFormGroup;
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly pageSizeControl: FormControl;
  protected readonly logsheetOptions = SUBSTATION_LOGSHEET_OPTIONS;
  protected readonly gridDivisionOptions = SUBSTATION_GRID_DIVISION_OPTIONS;
  protected readonly provinceFilterInputId = 'manage-substation-province-filter';
  protected readonly districtFilterInputId = 'manage-substation-district-filter';

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected isExporting = false;
  protected isDownloadingTemplate = false;
  protected isImporting = false;
  protected isSubstationActionMenuOpen = false;
  protected substationModalOpen = false;
  protected deleteModalOpen = false;
  protected substationGeneratorsModalOpen = false;
  protected substationGeneratorPickerModalOpen = false;
  protected isSubstationGeneratorsLoading = false;
  protected isAvailableGeneratorsLoading = false;
  protected isGeneratorMappingSaving = false;
  protected modalMode: ManageSubstationModalMode = 'create';
  protected selectedSubstation: ManageSubstationItem | null = null;
  protected selectedSubstationForGenerators: ManageSubstationItem | null = null;
  protected selectedAvailableGeneratorIds = new Set<number>();

  protected substations: ManageSubstationItem[] = [];
  protected filteredSubstations: ManageSubstationItem[] = [];
  protected substationGenerators: ManageSubstationGeneratorItem[] = [];
  protected availableGenerators: ManageSubstationGeneratorItem[] = [];
  protected pagination: ManageSubstationPagination = {
    page_no: 1,
    page_size: 15,
    total_records: 0,
    total_pages: 0,
  };

  protected states: DropdownOption[] = [];
  protected districts: DropdownOption[] = [];
  protected districtsLoading = false;
  protected provinceOptions: DropdownOption[] = [];
  protected filteredProvinceOptions: DropdownOption[] = [];
  protected districtFilterOptions: DropdownOption[] = [];
  protected filteredDistrictFilterOptions: DropdownOption[] = [];
  protected provinceSearchTerm: string | DropdownOption = '';
  protected districtSearchTerm: string | DropdownOption = '';
  protected voltageLevels: DropdownOption[] = [];
  protected sourceCategories: string[] = [];
  protected subCategoryOptions: string[] = [];
  protected formSubmitted = false;
  protected sortBy: ManageSubstationSortField | null = null;
  protected sortOrder: ManageSubstationSortOrder = 'asc';

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private allDistricts: ManageSubstationDistrictOption[] = [];

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly commonSvc: Commonservice,
  ) {
    this.filterForm = this.fb.group({
      search: [''],
      province_id: [null],
      district_id: [null],
      grid_division: [SUBSTATION_ALL_FILTER_VALUE],
      page_size: [15],
    });
    this.pageSizeControl = this.filterForm.get('page_size') as FormControl;

    this.substationForm = this.fb.group({
      station_name: ['', [trimmedRequiredValidator, Validators.maxLength(255)]],
      station_code: ['', [Validators.required, Validators.maxLength(25)]],
      scada_code: ['', [Validators.maxLength(50)]],
      state_id: [null],
      district_id: [null],
      operational_status: ['', [Validators.maxLength(100)]],
      capacity: [null],
      total_capacitors: [null],
      total_reactors: [null],
      total_transformers: [null],
      grid_division: ['', [Validators.maxLength(100)]],
      logsheet: [true],
      lat: ['', [Validators.maxLength(100), coordinateRangeValidator(-90, 90)]],
      long: ['', [Validators.maxLength(100), coordinateRangeValidator(-180, 180)]],
      connectivity: ['', [Validators.maxLength(50)]],
      voltage_level_id: [null],
    });
  }

  ngOnInit(): void {
    this.loadLookupData();
    this.loadSubstations(1);

    this.filterForm.valueChanges
      .pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadSubstations(1));

    const stateControl = this.substationForm.get('state_id');
    stateControl?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((stateId) => this.onStateSelectionChange(stateId));

  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected clearFilters(): void {
    this.filterForm.reset(
      {
        search: '',
        province_id: null,
        district_id: null,
        grid_division: SUBSTATION_ALL_FILTER_VALUE,
        page_size: this.pageSizeControl.value ?? 15,
      },
      { emitEvent: false },
    );
    this.isSubstationActionMenuOpen = false;
    this.provinceSearchTerm = '';
    this.districtSearchTerm = '';
    this.filteredProvinceOptions = [...this.provinceOptions];
    this.loadDistrictFilterOptions();
    this.loadSubstations(1);
  }

  protected loadLookupData(): void {
    this.loadStates();
    this.loadAllDistricts();
    this.loadVoltageLevels();
  }

  protected loadStates(): void {
    this.httpSvc
      .requestCall(AuthEndPoints.GET_STATES_LIST, ApiMethod.GET, '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.states = (res?.value?.states ?? res?.states ?? []).map((state: any) => ({
            id: state.id,
            name: state.state_name,
          }));
          this.provinceOptions = [...this.states];
          this.filteredProvinceOptions = [...this.provinceOptions];
          this.loadDistrictFilterOptions();
        },
        error: (err) => {
          console.error(SUBSTATION_FAILED_LOAD_STATES_MESSAGE, err);
          this.states = [];
          this.provinceOptions = [];
          this.filteredProvinceOptions = [];
        },
      });
  }

  protected loadVoltageLevels(): void {
    this.httpSvc
      .requestCall(AuthEndPoints.GET_VOLTAGE_LEVELS_LIST, ApiMethod.GET, '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.voltageLevels = (res?.value?.voltage_levels ?? res?.voltage_levels ?? []).map((voltage: any) => ({
            id: voltage.id,
            name: `${voltage.level} ${voltage.unit}`,
          }));
        },
        error: (err) => {
          console.error(SUBSTATION_FAILED_LOAD_VOLTAGE_LEVELS_MESSAGE, err);
          this.voltageLevels = [];
        },
      });
  }

  protected loadDistricts(stateId: number | null, selectedDistrictId: number | null = null): void {
    if (stateId === null || stateId === undefined) {
      this.districts = [];
      this.districtsLoading = false;
      if (selectedDistrictId === null) {
        this.substationForm.patchValue({ district_id: null }, { emitEvent: false });
      }
      return;
    }

    if (this.allDistricts.length) {
      this.setDistrictsForState(stateId, selectedDistrictId);
      return;
    }

    this.districtsLoading = true;
    const query = this.buildQueryString({ state_id: stateId });

    this.httpSvc
      .requestCall(AuthEndPoints.GET_DISTRICTS_LIST, ApiMethod.GET, query)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.districts = this.mapDistrictOptions(res?.value?.districts ?? res?.districts ?? []);
          this.districtsLoading = false;

          if (selectedDistrictId !== null && selectedDistrictId !== undefined) {
            this.substationForm.patchValue({ district_id: selectedDistrictId }, { emitEvent: false });
          }
        },
        error: (err) => {
          console.error(SUBSTATION_FAILED_LOAD_DISTRICTS_MESSAGE, err);
          this.districts = [];
          this.districtsLoading = false;
        },
      });
  }

  protected get isDistrictSelectionDisabled(): boolean {
    return !this.substationForm.get('state_id')?.value || this.districtsLoading;
  }

  private loadAllDistricts(): void {
    this.httpSvc
      .requestCall(AuthEndPoints.GET_DISTRICTS_LIST, ApiMethod.GET, '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.allDistricts = this.mapDistrictOptions(res?.value?.districts ?? res?.districts ?? []);
          const selectedStateId = this.substationForm.get('state_id')?.value;
          if (selectedStateId !== null && selectedStateId !== undefined) {
            this.setDistrictsForState(selectedStateId, this.substationForm.get('district_id')?.value ?? null);
          }
        },
        error: (err) => {
          console.error(SUBSTATION_FAILED_LOAD_DISTRICTS_MESSAGE, err);
          this.allDistricts = [];
        },
      });
  }

  private setDistrictsForState(stateId: number, selectedDistrictId: number | null): void {
    this.districts = this.allDistricts
      .filter((district) => district.stateId === stateId)
      .map(({ id, name }) => ({ id, name }));
    this.districtsLoading = false;

    if (selectedDistrictId !== null && selectedDistrictId !== undefined) {
      this.substationForm.patchValue({ district_id: selectedDistrictId }, { emitEvent: false });
    }
  }

  private mapDistrictOptions(districts: any[]): ManageSubstationDistrictOption[] {
    return districts.map((district: any) => ({
      id: district.id,
      name: district.district_name,
      stateId: district.state_id,
    }));
  }


  protected onStateSelectionChange(stateId: number | null): void {
    if (stateId === null || stateId === undefined) {
      this.districts = [];
      this.substationForm.patchValue({ district_id: null }, { emitEvent: false });
      return;
    }

    this.substationForm.patchValue({ district_id: null }, { emitEvent: false });
    this.districtsLoading = true;
    this.loadDistricts(stateId);
  }

  protected onProvinceSearchInput(event: ManageSubstationAutocompleteEvent): void {
    const value = this.extractAutocompleteValue(event);
    const normalizedValue = value.trim();

    this.provinceSearchTerm = value;
    this.filterProvinceAutocomplete(value);

    if (!normalizedValue) {
      this.clearProvinceFilterSelection();
      this.loadSubstations(1);
      return;
    }

    const matchedProvince = this.findProvinceByName(normalizedValue);
    if (!matchedProvince) {
      return;
    }

    this.provinceSearchTerm = matchedProvince.name;
    this.filterForm.patchValue(
      {
        province_id: matchedProvince.id,
        district_id: null,
      },
      { emitEvent: false },
    );
    this.districtSearchTerm = '';
    this.loadDistrictFilterOptions(matchedProvince.id);
    this.loadSubstations(1);
  }

  protected onProvinceSearchBlur(): void {
    if (typeof window === 'undefined') {
      this.commitProvinceFilterAutocompleteValue();
      return;
    }

    window.setTimeout(() => {
      this.commitProvinceFilterAutocompleteValue();
      this.cdr.markForCheck();
    }, 140);
  }

  protected onDistrictSearchInput(event: ManageSubstationAutocompleteEvent): void {
    const value = this.extractAutocompleteValue(event);
    const normalizedValue = value.trim();

    this.districtSearchTerm = value;
    this.filterDistrictAutocomplete(value);

    if (!normalizedValue) {
      this.clearDistrictFilterSelection();
      this.loadSubstations(1);
      return;
    }

    const matchedDistrict = this.findDistrictByName(normalizedValue);
    if (!matchedDistrict) {
      return;
    }

    this.districtSearchTerm = matchedDistrict.name;
    this.filterForm.patchValue({ district_id: matchedDistrict.id }, { emitEvent: false });
    this.loadSubstations(1);
  }

  protected onDistrictSearchBlur(): void {
    if (typeof window === 'undefined') {
      this.commitDistrictFilterAutocompleteValue();
      return;
    }

    window.setTimeout(() => {
      this.commitDistrictFilterAutocompleteValue();
      this.cdr.markForCheck();
    }, 140);
  }

  protected loadSubstations(pageNo = this.pagination.page_no): void {
    this.isLoading = true;
    const query = this.buildQueryString({
      search: this.filterForm.get('search')?.value,
      province_id: this.filterForm.get('province_id')?.value,
      district_id: this.filterForm.get('district_id')?.value,
      grid_division: this.filterForm.get('grid_division')?.value,
      page_no: pageNo,
      page_size: this.filterForm.get('page_size')?.value,
      sort_by: this.sortBy,
      sort_order: this.sortBy ? this.sortOrder : null,
    });

    this.httpSvc
      .requestCall(AuthEndPoints.GET_SUBSTATIONS_LIST, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res) => {
          this.substations = this.mapSubstationList(res?.value?.substations ?? res?.substations ?? []);
          this.filteredSubstations = [...this.substations];
          this.pagination = this.extractPagination(res?.value) ?? this.pagination;
        },
        error: (err) => {
          console.error(SUBSTATION_FAILED_LOAD_SUBSTATIONS_MESSAGE, err);
          this.substations = [];
          this.filteredSubstations = [];
          this.pagination = {
            page_no: pageNo,
            page_size: Number(this.filterForm.get('page_size')?.value ?? 10),
            total_records: 0,
            total_pages: 0,
          };
        },
      });
  }

  protected handleExportSubstations(): void {
    if (this.isExporting || this.isDownloadingTemplate || this.isImporting) {
      return;
    }

    this.isSubstationActionMenuOpen = false;
    this.isExporting = true;
    const query = this.buildQueryString({
      search: this.filterForm.get('search')?.value,
      province_id: this.filterForm.get('province_id')?.value,
      district_id: this.filterForm.get('district_id')?.value,
      grid_division: this.filterForm.get('grid_division')?.value,
      page_no: this.pagination.page_no,
      page_size: this.pagination.page_size,
    });

    this.httpSvc
      .downloadfile(AuthEndPoints.EXPORT_SUBSTATIONS, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isExporting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => this.downloadBlob(blob, this.buildExportFileName('substations')),
        error: (error) => this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || SUBSTATION_FAILED_EXPORT_MESSAGE),
      });
  }

  protected handleDownloadTemplate(): void {
    if (this.isExporting || this.isDownloadingTemplate || this.isImporting) {
      return;
    }

    this.isSubstationActionMenuOpen = false;
    this.isDownloadingTemplate = true;

    this.httpSvc
      .downloadfile(AuthEndPoints.SUBSTATION_TEMPLATE, ApiMethod.GET)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isDownloadingTemplate = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => this.downloadBlob(blob, 'substation_import_template.xlsx'),
        error: (error) => this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || SUBSTATION_FAILED_TEMPLATE_MESSAGE),
      });
  }

  protected triggerSubstationImportFilePicker(fileInput: HTMLInputElement | null): void {
    if (!fileInput || this.isImporting || this.isExporting || this.isDownloadingTemplate) {
      return;
    }

    this.isSubstationActionMenuOpen = false;
    fileInput.click();
  }

  protected toggleSubstationActionMenu(): void {
    if (this.isLoading || this.isExporting || this.isDownloadingTemplate || this.isImporting) {
      return;
    }

    this.isSubstationActionMenuOpen = !this.isSubstationActionMenuOpen;
  }

  protected onSubstationImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.length ? input.files[0] : null;

    if (!file) {
      return;
    }

    const fileName = file.name || '';
    if (!fileName.toLowerCase().endsWith('.xlsx')) {
      this.commonSvc.showErrorToastr(SUBSTATION_ONLY_XLSX_MESSAGE);
      if (input) {
        input.value = '';
      }
      return;
    }

    this.isImporting = true;

    const formData = new FormData();
    formData.append('file', file);

    this.httpSvc
      .requestCall(AuthEndPoints.IMPORT_SUBSTATIONS, ApiMethod.POST, formData, true)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isImporting = false;
          if (input) {
            input.value = '';
          }
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.loadSubstations(1);
        }
      });
  }

  protected getStateName(id: number | null): string {
    if (id === null || id === undefined) {
      return '-';
    }
    return this.states.find((s) => s.id === id)?.name ?? '-';
  }

  protected getDistrictName(id: number | null): string {
    if (id === null || id === undefined) {
      return '-';
    }
    return this.districts.find((d) => d.id === id)?.name ?? '-';
  }

  protected openSubstationGeneratorsModal(substation: ManageSubstationItem): void {
    this.selectedSubstationForGenerators = substation;
    this.substationGeneratorPickerModalOpen = false;
    this.substationGeneratorsModalOpen = true;
    this.substationGenerators = [];
    this.availableGenerators = [];
    this.selectedAvailableGeneratorIds.clear();
    this.loadSubstationGenerators(substation.id);
  }

  protected openAddNewGeneratorModal(): void {
    if (!this.selectedSubstationForGenerators) {
      return;
    }

    this.substationGeneratorPickerModalOpen = true;
    this.selectedAvailableGeneratorIds.clear();
    this.loadAvailableGenerators(this.selectedSubstationForGenerators.id);
  }

  protected closeSubstationGeneratorsModal(): void {
    this.substationGeneratorsModalOpen = false;
    this.substationGeneratorPickerModalOpen = false;
    this.selectedSubstationForGenerators = null;
    this.substationGenerators = [];
    this.availableGenerators = [];
    this.selectedAvailableGeneratorIds.clear();
  }

  protected closeGeneratorPickerModal(): void {
    this.substationGeneratorPickerModalOpen = false;
    this.availableGenerators = [];
    this.selectedAvailableGeneratorIds.clear();
  }

  protected getSubstationGeneratorCountLabel(count: number): string {
    return `${count}`;
  }

  protected trackByGeneratorId(_: number, item: ManageSubstationGeneratorItem): number {
    return item.id;
  }

  protected isAvailableGeneratorSelected(generatorId: number): boolean {
    return this.selectedAvailableGeneratorIds.has(generatorId);
  }

  protected toggleAvailableGeneratorSelection(generatorId: number, checked: boolean): void {
    if (checked) {
      this.selectedAvailableGeneratorIds.add(generatorId);
      return;
    }

    this.selectedAvailableGeneratorIds.delete(generatorId);
  }

  protected saveSelectedGenerators(): void {
    if (!this.selectedSubstationForGenerators || this.isGeneratorMappingSaving) {
      return;
    }

    const generatorIds = Array.from(this.selectedAvailableGeneratorIds);
    if (!generatorIds.length) {
      return;
    }

    this.isGeneratorMappingSaving = true;
    const substationId = this.selectedSubstationForGenerators.id;
    const requests = generatorIds.map((generatorId) =>
      this.httpSvc.requestCall(
        AuthEndPoints.MAP_GENERATOR_TO_SUBSTATION,
        ApiMethod.POST,
        {
          substation_id: substationId,
          generator_id: generatorId,
        },
        false,
        { suppressToast: true },
      ),
    );

    forkJoin(requests)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isGeneratorMappingSaving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.closeGeneratorPickerModal();
          this.loadSubstationGenerators(substationId);
          this.loadAvailableGenerators(substationId);
          this.loadSubstations(this.pagination.page_no);
        },
        error: (error) => {
          this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || 'Failed to assign generators');
        },
      });
  }

  protected unmapGeneratorFromSelectedSubstation(generator: ManageSubstationGeneratorItem): void {
    if (!this.selectedSubstationForGenerators || this.isGeneratorMappingSaving) {
      return;
    }

    this.isGeneratorMappingSaving = true;
    const payload = {
      substation_id: this.selectedSubstationForGenerators.id,
      generator_id: generator.id,
    };

    this.httpSvc
      .requestCall(AuthEndPoints.UNMAP_GENERATOR_FROM_SUBSTATION, ApiMethod.POST, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isGeneratorMappingSaving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.loadSubstationGenerators(this.selectedSubstationForGenerators?.id ?? 0);
          this.loadAvailableGenerators(this.selectedSubstationForGenerators?.id ?? 0);
          this.loadSubstations(this.pagination.page_no);
        },
      });
  }

  protected openAddSubstationModal(): void {
    this.modalMode = 'create';
    this.selectedSubstation = null;
    this.substationForm.enable({ emitEvent: false });
    this.substationForm.reset(createManageSubstationFormValue(), { emitEvent: false });
    this.districts = [];
    this.formSubmitted = false;
    this.substationModalOpen = true;
  }

  protected openEditSubstationModal(substation: ManageSubstationItem): void {
    this.modalMode = 'edit';
    this.selectedSubstation = substation;
    this.substationForm.enable({ emitEvent: false });
    this.substationForm.reset({
      station_name: substation.station_name,
      station_code: substation.station_code ?? '',
      scada_code: substation.scada_code ?? '',
      state_id: substation.state_id,
      district_id: substation.district_id,
      operational_status: substation.operational_status ?? '',
      capacity: substation.capacity,
      total_capacitors: substation.total_capacitors,
      total_reactors: substation.total_reactors,
      total_transformers: substation.total_transformers,
      grid_division: substation.grid_division ?? '',
      logsheet: substation.logsheet,
      lat: substation.lat ?? '',
      long: substation.long ?? '',
      connectivity: substation.connectivity ?? '',
      voltage_level_id: substation.voltage_level_id,
    }, { emitEvent: false });
    this.loadDistricts(substation.state_id, substation.district_id);
    this.formSubmitted = false;
    this.substationModalOpen = true;
  }

  protected openViewSubstationModal(substation: ManageSubstationItem): void {
    this.modalMode = 'view';
    this.selectedSubstation = substation;
    this.substationForm.enable({ emitEvent: false });
    this.substationForm.reset({
      station_name: substation.station_name,
      station_code: substation.station_code ?? '',
      scada_code: substation.scada_code ?? '',
      state_id: substation.state_id,
      district_id: substation.district_id,
      operational_status: substation.operational_status ?? '',
      capacity: substation.capacity,
      total_capacitors: substation.total_capacitors,
      total_reactors: substation.total_reactors,
      total_transformers: substation.total_transformers,
      grid_division: substation.grid_division ?? '',
      logsheet: substation.logsheet,
      lat: substation.lat ?? '',
      long: substation.long ?? '',
      connectivity: substation.connectivity ?? '',
      voltage_level_id: substation.voltage_level_id,
    }, { emitEvent: false });
    this.loadDistricts(substation.state_id, substation.district_id);
    this.formSubmitted = false;
    this.substationModalOpen = true;
    this.substationForm.disable({ emitEvent: false });
  }

  protected openDeleteSubstationModal(substation: ManageSubstationItem): void {
    this.selectedSubstation = substation;
    this.deleteModalOpen = true;
  }

  protected closeSubstationModal(): void {
    this.substationModalOpen = false;
    this.selectedSubstation = null;
    this.modalMode = 'create';
    this.substationForm.enable({ emitEvent: false });
    this.substationForm.reset(createManageSubstationFormValue(), { emitEvent: false });
    this.districts = [];
    this.formSubmitted = false;
  }

  protected closeDeleteModal(): void {
    this.deleteModalOpen = false;
    this.selectedSubstation = null;
  }

  protected saveSubstation(): void {
    if (this.modalMode === 'view') {
      return;
    }

    this.trimStationName();
    if (this.substationForm.invalid) {
      this.formSubmitted = true;
      this.substationForm.markAllAsTouched();
      return;
    }

    const payload = this.buildSubstationPayload();
    const apiRoute = this.modalMode === 'create' ? AuthEndPoints.ADD_SUBSTATION : AuthEndPoints.EDIT_SUBSTATION;
    const method = this.modalMode === 'create' ? ApiMethod.POST : ApiMethod.PATCH;

    this.httpSvc
      .requestCall(apiRoute, method, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeSubstationModal();
          this.loadSubstations();
        },
      });
  }

  protected confirmDelete(): void {
    if (!this.selectedSubstation) {
      return;
    }

    const payload = { id: this.selectedSubstation.id };

    this.httpSvc
      .requestCall(AuthEndPoints.DELETE_SUBSTATION, ApiMethod.DELETE, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeDeleteModal();
          this.loadSubstations();
        },
      });
  }

  protected trackBySubstationId(_index: number, item: ManageSubstationItem): number {
    return item.id;
  }

  protected toggleSort(field: ManageSubstationSortField): void {
    this.sortOrder = this.sortBy === field && this.sortOrder === 'asc' ? 'desc' : 'asc';
    this.sortBy = field;
    this.loadSubstations(1);
  }

  protected isSortAscending(field: ManageSubstationSortField): boolean { return this.sortBy === field && this.sortOrder === 'asc'; }
  protected isSortDescending(field: ManageSubstationSortField): boolean { return this.sortBy === field && this.sortOrder === 'desc'; }

  protected isRequiredFieldInvalid(fieldName: string): boolean {
    const control = this.substationForm.get(fieldName);
    return Boolean(control?.hasError('required') && (control.touched || this.formSubmitted));
  }

  protected trimStationName(): void {
    const control = this.substationForm.get('station_name');
    if (typeof control?.value !== 'string') {
      return;
    }

    const trimmedName = control.value.trim();
    if (control.value !== trimmedName) {
      control.setValue(trimmedName);
    }
  }

  protected getRequiredFieldError(fieldName: string, message: string): string {
    return this.isRequiredFieldInvalid(fieldName) ? message : '\u00A0';
  }

  protected canShowPagination(): boolean {
    return canShowPagination(this.pagination.total_records);
  }

  protected getPaginationLabel(): string {
    return buildPaginationLabel(this.pagination, SUBSTATION_NO_RECORDS_LABEL, SUBSTATION_SHOWING_RESULTS_LABEL, SUBSTATION_RESULTS_LABEL);
  }

  protected getPaginationItems(): number[] {
    return buildPaginationItems(this.pagination.total_pages, this.pagination.page_no);
  }

  protected goToPreviousPage(): void { this.goToPage(this.pagination.page_no - 1); }
  protected goToNextPage(): void { this.goToPage(this.pagination.page_no + 1); }
  protected goToPage(pageNo: number): void {
    if (pageNo < 1 || pageNo > this.pagination.total_pages) return;
    this.loadSubstations(pageNo);
  }

  private buildSubstationPayload(): Partial<ManageSubstationFormValue> & { id?: number } {
    const formValue: ManageSubstationFormValue = this.substationForm.getRawValue();

    const payload: Partial<ManageSubstationFormValue> & { id?: number } = { ...formValue };
    if (this.modalMode === 'edit' && this.selectedSubstation) {
      delete payload.station_code;
      payload.id = this.selectedSubstation.id;
    }

    return payload;
  }

  private loadSubstationGenerators(substationId: number): void {
    if (!substationId) {
      this.substationGenerators = [];
      return;
    }

    this.isSubstationGeneratorsLoading = true;
    const query = this.buildQueryString({ substation_id: substationId });
    this.httpSvc
      .requestCall(AuthEndPoints.GET_SUBSTATION_GENERATORS_LIST, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isSubstationGeneratorsLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res) => {
          this.substationGenerators = this.mapGeneratorList(res?.value?.generators ?? res?.generators ?? []);
          if (this.selectedSubstationForGenerators) {
            this.selectedSubstationForGenerators = {
              ...this.selectedSubstationForGenerators,
              generator_count: this.substationGenerators.length,
            };
          }
        },
        error: () => {
          this.substationGenerators = [];
        },
      });
  }

  private commitProvinceFilterAutocompleteValue(): void {
    const trimmedValue = this.getAutocompleteText(this.provinceSearchTerm).trim();
    if (!trimmedValue) {
      this.clearProvinceFilterSelection();
      this.loadSubstations(1);
      return;
    }

    const matchedProvince = this.findProvinceByName(trimmedValue);
    if (!matchedProvince) {
      this.clearProvinceFilterSelection();
      this.loadSubstations(1);
      return;
    }

    const selectionChanged =
      this.filterForm.get('province_id')?.value !== matchedProvince.id ||
      this.filterForm.get('district_id')?.value !== null;

    this.provinceSearchTerm = matchedProvince.name;
    this.filterForm.patchValue(
      {
        province_id: matchedProvince.id,
        district_id: null,
      },
      { emitEvent: false },
    );
    this.districtSearchTerm = '';
    this.loadDistrictFilterOptions(matchedProvince.id);

    if (selectionChanged) {
      this.loadSubstations(1);
    }
  }

  private commitDistrictFilterAutocompleteValue(): void {
    const trimmedValue = this.getAutocompleteText(this.districtSearchTerm).trim();
    if (!trimmedValue) {
      this.clearDistrictFilterSelection();
      this.loadSubstations(1);
      return;
    }

    const matchedDistrict = this.findDistrictByName(trimmedValue);
    if (!matchedDistrict) {
      this.clearDistrictFilterSelection();
      this.loadSubstations(1);
      return;
    }

    const selectionChanged = this.filterForm.get('district_id')?.value !== matchedDistrict.id;
    this.districtSearchTerm = matchedDistrict.name;
    this.filterForm.patchValue({ district_id: matchedDistrict.id }, { emitEvent: false });

    if (selectionChanged) {
      this.loadSubstations(1);
    }
  }

  private clearProvinceFilterSelection(): void {
    this.provinceSearchTerm = '';
    this.districtSearchTerm = '';
    this.filterForm.patchValue({ province_id: null, district_id: null }, { emitEvent: false });
    this.loadDistrictFilterOptions();
  }

  private clearDistrictFilterSelection(): void {
    this.districtSearchTerm = '';
    this.filterForm.patchValue({ district_id: null }, { emitEvent: false });
  }

  private filterProvinceAutocomplete(query: string): void {
    const normalizedQuery = query.trim().toLowerCase();
    this.filteredProvinceOptions = !normalizedQuery
      ? [...this.provinceOptions]
      : this.provinceOptions.filter((province) => province.name.toLowerCase().includes(normalizedQuery));
  }

  private filterDistrictAutocomplete(query: string): void {
    const normalizedQuery = query.trim().toLowerCase();
    this.filteredDistrictFilterOptions = !normalizedQuery
      ? [...this.districtFilterOptions]
      : this.districtFilterOptions.filter((district) => district.name.toLowerCase().includes(normalizedQuery));
  }

  private findProvinceByName(name: string): DropdownOption | undefined {
    const normalizedName = name.trim().toLowerCase();
    return this.provinceOptions.find((province) => province.name.trim().toLowerCase() === normalizedName);
  }

  private findDistrictByName(name: string): DropdownOption | undefined {
    const normalizedName = name.trim().toLowerCase();
    return this.districtFilterOptions.find((district) => district.name.trim().toLowerCase() === normalizedName);
  }

  private extractAutocompleteValue(event: ManageSubstationAutocompleteEvent): string {
    if ('query' in event && typeof event.query === 'string') {
      return event.query;
    }

    if ('value' in event && typeof event.value === 'string') {
      return event.value;
    }

    const input = (event as Event).target as HTMLInputElement | null;
    return input?.value ?? '';
  }

  private getAutocompleteText(value: string | DropdownOption): string {
    return typeof value === 'string' ? value : value.name;
  }

  private loadDistrictFilterOptions(stateId: number | null = null, selectedDistrictId: number | null = null): void {
    const query = stateId ? this.buildQueryString({ state_id: stateId }) : '';
    this.httpSvc
      .requestCall(AuthEndPoints.GET_DISTRICTS_LIST, ApiMethod.GET, query)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.districtFilterOptions = (res?.value?.districts ?? res?.districts ?? []).map((district: any) => ({
            id: district.id,
            name: district.district_name,
          }));
          this.filteredDistrictFilterOptions = [...this.districtFilterOptions];

          if (selectedDistrictId !== null && selectedDistrictId !== undefined) {
            const matchedDistrict = this.districtFilterOptions.find((district) => district.id === selectedDistrictId);
            if (matchedDistrict) {
              this.districtSearchTerm = matchedDistrict.name;
              this.filterForm.patchValue({ district_id: matchedDistrict.id }, { emitEvent: false });
            }
          }
        },
        error: (err) => {
          console.error(SUBSTATION_FAILED_LOAD_DISTRICTS_MESSAGE, err);
          this.districtFilterOptions = [];
          this.filteredDistrictFilterOptions = [];
        },
      });
  }

  private loadAvailableGenerators(substationId: number): void {
    if (!substationId) {
      this.availableGenerators = [];
      return;
    }

    this.isAvailableGeneratorsLoading = true;
    const query = this.buildQueryString({ substation_id: substationId });
    this.httpSvc
      .requestCall(AuthEndPoints.GET_SUBSTATION_AVAILABLE_GENERATORS_LIST, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isAvailableGeneratorsLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res) => {
          this.availableGenerators = this.mapGeneratorList(res?.value?.generators ?? res?.generators ?? []);
        },
        error: () => {
          this.availableGenerators = [];
        },
      });
  }


  private buildQueryString(params: ManageSubstationQueryParams): string {
    return buildQueryString(params, [SUBSTATION_ALL_FILTER_VALUE]);
  }

  private buildExportFileName(prefix: string): string {
    return buildTimestampFileName(prefix);
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    downloadBlob(blob, fileName);
  }

  private getApiErrorMessage(error: unknown): string | null {
    return getApiErrorMessage(error);
  }

  private extractPagination(value: unknown): ManageSubstationPagination | null {
    return extractPagination<ManageSubstationPagination>(value);
  }

  private toStr(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

private toBlankStr(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    const normalized = value.trim();
    return normalized && normalized.toLowerCase() !== 'none' ? normalized : '';
  }

  return '';
}

private toNullableStr(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

private toNum(value: unknown, fallback = 0): number {
  return Number(value ?? fallback);
}

private toNullableNum(value: unknown): number | null {
  return value === null || value === undefined ? null : Number(value);
}

private toBool(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'true';
  }

  return Boolean(value);
}

  private mapSubstationItem(record: Record<string, unknown>): ManageSubstationItem {
  return {
    id: this.toNum(record['id']),
    serial_no: this.toNum(record['serial_no']),
    generator_count: this.toNum(record['generator_count']),
    station_name: this.toStr(record['station_name'], 'Untitled substation'),
    station_code: this.toNullableStr(record['station_code']),
    scada_code: this.toNullableStr(record['scada_code']),
    state_id: this.toNullableNum(record['state_id']),
    state_name: this.toNullableStr(record['state_name']),
    district_id: this.toNullableNum(record['district_id']),
    district_name: this.toNullableStr(record['district_name']),
    operational_status: this.toNullableStr(record['operational_status']),
    capacity: this.toNullableNum(record['capacity']),
    total_capacitors: this.toNullableNum(record['total_capacitors']),
    total_reactors: this.toNullableNum(record['total_reactors']),
    total_transformers: this.toNullableNum(record['total_transformers']),
    grid_division: this.toStr(record['grid_division'], ''),
    logsheet: this.toBool(record['logsheet']),
    lat: this.toNullableStr(record['lat']),
    long: this.toNullableStr(record['long']),
    connectivity: this.toNullableStr(record['connectivity']),
    voltage_level_id: this.toNullableNum(record['voltage_level_id']),
    voltage_level_label: this.toNullableStr(record['voltage_level_label']),
  };
}

  private mapGeneratorItem(record: Record<string, unknown>): ManageSubstationGeneratorItem {
  return {
    id: this.toNum(record['id']),
    serial_no: this.toNum(record['serial_no']),
    generator_name: this.toStr(record['generator_name'], 'Untitled generator'),
    generator_code: this.toNullableStr(record['generator_code']),
    generator_type: this.toNullableStr(record['generator_type']),
    organization_type: this.toNullableStr(record['organization_type']),
    voltage_level_label: this.toNullableStr(record['voltage_level_label']),
    connecting_voltage_level_label: this.toNullableStr(record['connecting_voltage_level_label']),
    capacity: this.toNullableNum(record['capacity']),
    substation_id: this.toNum(record['substation_id']),
    substation_name: this.toNullableStr(record['substation_name']),
  };
}

private mapGeneratorList(items: unknown): ManageSubstationGeneratorItem[] {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item) => this.mapGeneratorItem(item as Record<string, unknown>));
}

private mapSubstationList(items: unknown): ManageSubstationItem[] {
  if (!Array.isArray(items)) {
    return [];
  }
  return items.map((item) => this.mapSubstationItem(item as Record<string, unknown>));
}

}
